import React from 'react';
import { RouteComponentProps } from 'react-router';
import Card from '../components/Card';
import Carousel from '../components/Carousel';
import TagsHoc from '../components/hoc/TagsHoc';
import { useData } from '../context/DataContext';
import { useFavorites } from '../context/FavoritesContext';
import { useQueryStrings } from '../context/QueryStringContext';
import Popup from '../components/Popup';
import SimilarTimesHoc from '../components/hoc/SimilarTimesHoc';
import OtherTimes from '../components/OtherTimes';
import { useApp } from '../context/AppContext';
import { getOptimizedEdpImageUrl } from '../../../../../../../shared/lib/image';
import { DailyProgramVenue } from '../../../../../../../shared/types/dailyProgram';

interface VenueDetailsPagePropsBase {
  venue?: DailyProgramVenue;
}

type VenueDetailsPageProps = VenueDetailsPagePropsBase & RouteComponentProps;

export default function VenueDetailsPage(props: VenueDetailsPageProps) {
  const { todayLineup } = useData();
  const { favorites, toggleFavorite } = useFavorites();
  const { updateQs } = useQueryStrings();
  const { config } = useApp();

  const { venue } = props;
  const events = venue ? todayLineup.events.filter(evt => evt.venueId === venue.templateId) : [];
  const offers = venue
    ? todayLineup.offers.filter(offer => offer.venueId === venue.templateId)
    : [];

  return (
    <Popup onClose={() => updateQs('venue', undefined)}>
      {venue && (
        <div className="border-b border-slate-300">
          <TagsHoc type="venue" item={venue} key={venue.templateId}>
            {props => (
              <Card
                title={venue.venueString}
                subtitle={venue.timeStrings}
                description={venue.description}
                image={getOptimizedEdpImageUrl(
                  venue.images,
                  venue.imageOptimizations,
                  config.imgRequirements,
                  0
                )}
                tags={props.tags}
                isFavorite={!!favorites.venues[venue.templateId]}
                onFavoriteClick={() => toggleFavorite({ type: 'venues', value: venue })}
                detailed
                callToActions={venue.callToActions}
              />
            )}
          </TagsHoc>
          <hr className="border-slate-300 divider" />
          <div className="py-4 bg-white similar-container">
            <SimilarTimesHoc type="venues" item={venue}>
              {({ similarTimes }) => <OtherTimes similarTimes={similarTimes} />}
            </SimilarTimesHoc>
          </div>
          {events.length > 0 && config.enabledViews.includes('lineup') && (
            <Carousel
              icon={['far', 'calendar-day']}
              title="Events"
              className="carousel-container py-4 border-t border-slate-300"
            >
              {events.map(evt => (
                <TagsHoc type="event" item={evt} key={evt.id}>
                  {props => (
                    <Card
                      className="carousel border"
                      onClick={() => updateQs('event', evt.id)}
                      title={evt.title}
                      subtitle={[evt.venueString, evt.timeString]}
                      image={getOptimizedEdpImageUrl(
                        evt.images,
                        evt.imageOptimizations,
                        config.imgRequirements,
                        0
                      )}
                      tags={props.tags}
                      isFavorite={!!favorites.events[evt.id]}
                      onFavoriteClick={() => toggleFavorite({ type: 'events', value: evt })}
                    />
                  )}
                </TagsHoc>
              ))}
            </Carousel>
          )}
          {offers.length > 0 && config.enabledViews.includes('offers') && (
            <Carousel
              title="Offers"
              icon={['far', 'hand-holding-circle-dollar']}
              className={`carousel-container py-4 border-slate-300${
                events.length < 1 ? ' border-t' : ''
              }`}
            >
              {offers.map(offer => (
                <TagsHoc type="basic" item={offer} key={offer.id}>
                  {props => (
                    <Card
                      className="carousel border"
                      onClick={() => updateQs('offer', offer.id)}
                      title={offer.title}
                      subtitle={offer.description}
                      image={getOptimizedEdpImageUrl(
                        offer.images,
                        offer.imageOptimizations,
                        config.imgRequirements,
                        0
                      )}
                      tags={props.tags}
                      isFavorite={!!favorites.offers[offer.id]}
                      onFavoriteClick={() => toggleFavorite({ type: 'offers', value: offer })}
                    />
                  )}
                </TagsHoc>
              ))}
            </Carousel>
          )}
        </div>
      )}
    </Popup>
  );
}
